import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { SectionHead } from "@/components/site/Reveal";

const CountUp = ({ target, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1400;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, target]);

  return (
    <span ref={ref} className="font-display font-extrabold text-5xl md:text-6xl text-gradient tracking-tight">
      {value}€{suffix}
    </span>
  );
};

const plans = [
  {
    title: "Création du site",
    price: 850,
    suffix: "",
    caption: "Paiement unique — votre site clé en main",
    features: ["Design moderne sur mesure", "Aperçu gratuit avant engagement", "Optimisation SEO sur vos mots-clés", "Adapté mobile, tablette, ordinateur"],
    badge: null,
  },
  {
    title: "Hébergement & entretien",
    price: 65,
    suffix: "/mois",
    caption: "Votre site vit, évolue et reste en ligne",
    features: ["Hébergement inclus", "Entretien du site", "Mises à jour de pages", "Changements & modifications"],
    badge: "Abonnement mensuel",
  },
];

export const Tarifs = () => (
  <section id="tarifs" className="relative py-24 md:py-36" data-testid="tarifs-section">
    <div className="glow-orb w-[480px] h-[480px] bg-violet-600/10 bottom-0 -left-48" />
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <SectionHead
        label="Tarifs"
        title={
          <>
            Simple. <span className="text-gradient">Transparent.</span>
          </>
        }
        subtitle="Pas d'abonnement impersonnel, pas de coût caché. Une création sur mesure, puis un accompagnement humain dans la durée."
        align="center"
      />
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {plans.map((plan, i) => (
          <motion.div
            key={plan.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.75, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="relative card-surface rounded-3xl p-8 md:p-10"
            data-testid={`tarif-card-${i}`}
          >
            {plan.badge && (
              <span className="absolute -top-3 left-8 rounded-full btn-gradient px-4 py-1 font-mono-label text-[9px] tracking-[0.2em] uppercase text-white">
                {plan.badge}
              </span>
            )}
            <h3 className="font-display font-bold text-xl tracking-tight text-[var(--text-secondary)]">{plan.title}</h3>
            <div className="mt-4 mb-2">
              <CountUp target={plan.price} suffix={plan.suffix} />
            </div>
            <p className="text-sm text-[var(--text-muted)]">{plan.caption}</p>
            <ul className="mt-7 flex flex-col gap-3.5">
              {plan.features.map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm md:text-base text-white/85">
                  <Check size={15} className="text-[var(--accent-cyan)] flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
      <div className="text-center mt-12">
        <Link
          to="/demande"
          className="btn-gradient inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 font-semibold text-white"
          data-testid="tarifs-cta"
        >
          Demander mon aperçu gratuit <ArrowRight size={17} />
        </Link>
      </div>
    </div>
  </section>
);

export default Tarifs;
